import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

function TeacherGradeSelection() {
  const navigate = useNavigate();
  const { teacherId } = useParams();

  const [teacher, setTeacher] = useState(null);
  const [grades, setGrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  const [darkMode, setDarkMode] = useState(() => {
    try {
      return localStorage.getItem("teacher_demo_theme") !== "light";
    } catch (error) {
      return true;
    }
  });

  const [accountName, setAccountName] = useState("Kids");

  const TEACHER_API =
    "https://edunet.kmechengineering.com/root-engine1/api/teacher-demo-data";

  const GRADE_API =
    "https://edunet.kmechengineering.com/root-engine1/api/grades";

  useEffect(() => {
    try {
      localStorage.setItem(
        "teacher_demo_theme",
        darkMode ? "dark" : "light"
      );
    } catch (error) {}

    document.body.style.backgroundColor = darkMode
      ? "#141414"
      : "#ffffff";

    document.documentElement.style.backgroundColor = darkMode
      ? "#141414"
      : "#ffffff";

    return () => {
      document.body.style.backgroundColor = "";
      document.documentElement.style.backgroundColor = "";
    };
  }, [darkMode]);

  const getTeacherName = (item) => {
    return (
      item?.name ||
      item?.teacher_name ||
      item?.full_name ||
      "Teacher"
    );
  };

  const getGradeName = (item) => {
    return (
      item?.name ||
      item?.grade_name ||
      item?.grade ||
      item?.title ||
      ""
    );
  };

  const getGradeId = (item) => {
    return (
      item?.grade_id ??
      item?.gradeId ??
      item?.id ??
      null
    );
  };

  const normalize = (value) => {
    return String(value || "")
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  };

  useEffect(() => {
    const loadTeacherGrades = async () => {
      setLoading(true);
      setErrorMessage("");

      try {
        const [teacherResponse, gradeResponse] = await Promise.all([
          fetch(TEACHER_API),
          fetch(GRADE_API)
        ]);

        if (!teacherResponse.ok) {
          throw new Error("Failed to load teacher data");
        }

        if (!gradeResponse.ok) {
          throw new Error("Failed to load grade data");
        }

        const teacherJson = await teacherResponse.json();
        const gradeJson = await gradeResponse.json();

        const primary = Array.isArray(
          teacherJson?.teacher_data?.primary
        )
          ? teacherJson.teacher_data.primary
          : [];

        const middle = Array.isArray(
          teacherJson?.teacher_data?.middle
        )
          ? teacherJson.teacher_data.middle
          : [];

        const al = Array.isArray(
          teacherJson?.teacher_data?.al
        )
          ? teacherJson.teacher_data.al
          : [];

        const allTeachers = [
          ...primary,
          ...middle,
          ...al
        ];

        const selectedId = String(teacherId || "");

        const selectedRecords = allTeachers.filter((item) => {
          const id =
            item?.teacher_id ??
            item?.id ??
            "";

          return String(id) === selectedId;
        });

        if (selectedRecords.length === 0) {
          setTeacher(null);
          setGrades([]);
          setErrorMessage("Teacher not found.");
          return;
        }

        const teacherName = getTeacherName(selectedRecords[0]);

        setTeacher({
          teacher_id: selectedId,
          name: teacherName
        });

        // -----------------------------------------------------
        // IMPORTANT:
        // The teacherDemo API groups a teacher by subject.
        // A teacher can therefore have multiple records.
        // Collect all grades from all records and remove
        // duplicate grade names.
        // -----------------------------------------------------
        const gradeNameMap = new Map();

        selectedRecords.forEach((record) => {
          const recordGrades = Array.isArray(record?.grades)
            ? record.grades
            : [];

          recordGrades.forEach((gradeName) => {
            const key = normalize(gradeName);

            if (key && !gradeNameMap.has(key)) {
              gradeNameMap.set(key, gradeName);
            }
          });
        });

        // -----------------------------------------------------
        // Match teacher grade names with the real grade IDs.
        // This lets the next step use the existing
        // /subject-selection/{gradeId} flow.
        // -----------------------------------------------------
        const apiGrades = Array.isArray(gradeJson?.grade_data)
          ? gradeJson.grade_data
          : [];

        const mappedGrades = Array.from(gradeNameMap.values())
          .map((gradeName, index) => {
            const match = apiGrades.find((apiGrade) => {
              return (
                normalize(getGradeName(apiGrade)) ===
                normalize(gradeName)
              );
            });

            return {
              name: gradeName,
              grade_id: match ? getGradeId(match) : null,
              original_index: index
            };
          });

        setGrades(mappedGrades);
      } catch (error) {
        console.error("Teacher Grade API Error:", error);
        setTeacher(null);
        setGrades([]);
        setErrorMessage(
          "Unable to load grades for this teacher."
        );
      } finally {
        setLoading(false);
      }
    };

    loadTeacherGrades();
  }, [teacherId]);

  const handleGradeClick = (grade) => {
    if (!grade?.grade_id) {
      console.error("Grade ID is missing:", grade);
      return;
    }

    // Keep selected teacher available for the next flow.
    localStorage.setItem(
      "edunet_selected_teacher_id",
      String(teacherId)
    );

    localStorage.setItem(
      "edunet_selected_grade_id",
      String(grade.grade_id)
    );

    // Existing subject page is reused.
    // teacher_id is kept in the URL for the next filtering step.
    navigate(
      `/subject-selection/${encodeURIComponent(
        grade.grade_id
      )}?teacher_id=${encodeURIComponent(teacherId)}`
    );
  };

  const displayGrades = useMemo(() => {
    return grades;
  }, [grades]);

  return (
    <div
      className={`teacher-grade-page ${
        darkMode ? "dark-mode" : "light-mode"
      }`}
    >
      <style>{`
        * {
          box-sizing: border-box;
        }

        html,
        body {
          margin: 0;
          padding: 0;
          background: #141414;
        }

        body {
          overflow-x: hidden;
          font-family: Arial, Helvetica, sans-serif;
        }

        .teacher-grade-page {
          min-height: 100vh;
          padding: 0 48px 70px;
          background: #141414;
          color: #fff;
        }

        .teacher-grade-navbar {
          position: sticky;
          top: 0;
          z-index: 5000;
          height: 72px;
          margin: 0 -48px 40px;
          padding: 0 48px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          background: #050505;
          border-bottom: 1px solid rgba(255, 255, 255, 0.08);
          backdrop-filter: blur(12px);
        }

        .teacher-grade-navbar-left {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .teacher-grade-nav-button {
          border: none;
          background: transparent;
          color: rgba(255, 255, 255, 0.68);
          padding: 10px 17px;
          border-radius: 24px;
          font-size: 16px;
          cursor: pointer;
        }

        .teacher-grade-nav-button:hover,
        .teacher-grade-nav-button.active {
          background: #333338;
          color: #fff;
        }

        .teacher-grade-navbar-right {
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .teacher-grade-icon-button {
          width: 38px;
          height: 38px;
          border: none;
          border-radius: 50%;
          background: transparent;
          color: #fff;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0;
          transition: background 0.2s ease;
        }

        .teacher-grade-icon-button:hover,
        .teacher-grade-icon-button.active {
          background: #333338;
        }

        .teacher-grade-icon-button svg {
          width: 22px;
          height: 22px;
          display: block;
        }

        .teacher-grade-kids-item {
          height: 38px;
          min-width: 38px;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 7px;
          padding: 0 7px;
          color: #fff;
          background: transparent;
          border: none;
          cursor: pointer;
          border-radius: 6px;
          transition: background 0.2s ease;
          flex-shrink: 0;
        }

        .teacher-grade-kids-item:hover {
          background: #333338;
        }

        .teacher-grade-kids-logo {
          width: 32px;
          height: 32px;
          border-radius: 4px;
          overflow: hidden;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(
            135deg,
            #8d42b9 0%,
            #d34f9d 48%,
            #f3b33d 48%,
            #f3b33d 100%
          );
          flex-shrink: 0;
        }

        .teacher-grade-kids-logo span {
          color: #fff;
          font-size: 9px;
          font-weight: 800;
          background: rgba(220, 60, 170, 0.75);
          padding: 2px 1px;
        }

        .teacher-grade-kids-text {
          font-size: 16px;
          font-weight: 500;
          white-space: nowrap;
        }

        .teacher-grade-kids-arrow {
          width: 9px;
          height: 9px;
        }

        .teacher-grade-profile {
          width: 38px;
          height: 38px;
          border-radius: 6px;
          background: #f2c900;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #fff;
          cursor: pointer;
          transition: background 0.2s ease;
        }

        .teacher-grade-profile:hover {
          background: #dcb600;
        }

        .teacher-grade-content {
          max-width: 100%;
        }

        .teacher-grade-back {
          border: none;
          background: transparent;
          color: rgba(255, 255, 255, 0.65);
          font-size: 14px;
          cursor: pointer;
          padding: 0;
          margin-bottom: 25px;
        }

        .teacher-grade-back:hover {
          color: #fff;
        }

        .teacher-grade-label {
          color: #aaa;
          font-size: 13px;
          margin: 0 0 8px;
        }

        .teacher-grade-title {
          margin: 0;
          font-size: 30px;
          font-weight: 700;
        }

        .teacher-grade-subtitle {
          margin: 8px 0 28px;
          color: rgba(255, 255, 255, 0.6);
          font-size: 14px;
        }

        .teacher-grade-row-wrapper {
          overflow-x: auto;
          scrollbar-width: none;
          padding: 15px 0 30px;
        }

        .teacher-grade-row-wrapper::-webkit-scrollbar {
          display: none;
        }

        .teacher-grade-row {
          display: flex;
          gap: 14px;
          width: max-content;
        }

        .teacher-grade-card {
          width: 250px;
          height: 140px;
          flex: 0 0 250px;
          border-radius: 7px;
          position: relative;
          overflow: hidden;
          cursor: pointer;
          background: linear-gradient(
            135deg,
            #252525,
            #111
          );
          border: 1px solid rgba(255, 255, 255, 0.06);
          transition:
            transform 0.25s ease,
            box-shadow 0.25s ease;
        }

        .teacher-grade-card:hover {
          transform: scale(1.07);
          box-shadow: 0 15px 40px rgba(0, 0, 0, 0.75);
          z-index: 5;
        }

        .teacher-grade-card::after {
          content: "";
          position: absolute;
          inset: 0;
          background: linear-gradient(
            180deg,
            transparent 20%,
            rgba(0, 0, 0, 0.85) 100%
          );
          pointer-events: none;
        }

        .teacher-grade-number {
          position: absolute;
          top: 15px;
          left: 17px;
          z-index: 5;
          font-size: 13px;
          color: rgba(255, 255, 255, 0.65);
        }

        .teacher-grade-name {
          position: absolute;
          left: 17px;
          bottom: 16px;
          z-index: 5;
          font-size: 21px;
          font-weight: 700;
          color: #fff;
        }

        .teacher-grade-loading,
        .teacher-grade-empty {
          color: #999;
          padding: 30px 0;
        }

        /* LIGHT MODE */

        .teacher-grade-page.light-mode {
          background: #fff;
          color: #111;
        }

        .light-mode .teacher-grade-navbar {
          background: #fff;
          border-bottom-color: rgba(0, 0, 0, 0.1);
        }

        .light-mode .teacher-grade-nav-button {
          color: rgba(0, 0, 0, 0.65);
        }

        .light-mode .teacher-grade-nav-button:hover,
        .light-mode .teacher-grade-nav-button.active {
          background: #e5e5e5;
          color: #111;
        }

        .light-mode .teacher-grade-icon-button {
          color: #111;
        }

        .light-mode .teacher-grade-icon-button:hover {
          background: #e5e5e5;
        }

        .light-mode .teacher-grade-kids-item {
          color: #111;
        }

        .light-mode .teacher-grade-kids-item:hover {
          background: #e5e5e5;
        }

        .light-mode .teacher-grade-back {
          color: rgba(0, 0, 0, 0.6);
        }

        .light-mode .teacher-grade-back:hover {
          color: #111;
        }

        .light-mode .teacher-grade-label,
        .light-mode .teacher-grade-subtitle {
          color: rgba(0, 0, 0, 0.6);
        }

        .light-mode .teacher-grade-card {
          background: linear-gradient(
            135deg,
            #f4f4f4,
            #dddddd
          );
          border-color: rgba(0, 0, 0, 0.08);
        }

        .light-mode .teacher-grade-card::after {
          background: linear-gradient(
            180deg,
            transparent 20%,
            rgba(0, 0, 0, 0.18) 100%
          );
        }

        .light-mode .teacher-grade-number {
          color: rgba(0, 0, 0, 0.55);
        }

        .light-mode .teacher-grade-name {
          color: #111;
        }

        @media (max-width: 768px) {
          .teacher-grade-page {
            padding: 0 20px 50px;
          }

          .teacher-grade-navbar {
            margin: 0 -20px 30px;
            padding: 0 20px;
          }

          .teacher-grade-navbar-left {
            gap: 2px;
          }

          .teacher-grade-nav-button {
            padding: 9px 10px;
            font-size: 14px;
          }

          .teacher-grade-navbar-right {
            gap: 4px;
          }

          .teacher-grade-kids-text,
          .teacher-grade-kids-arrow {
            display: none;
          }

          .teacher-grade-card {
            width: 210px;
            height: 120px;
            flex-basis: 210px;
          }

          .teacher-grade-name {
            font-size: 18px;
          }
        }
      `}</style>

      <nav className="teacher-grade-navbar">
        <div className="teacher-grade-navbar-left">
          <button
            className="teacher-grade-nav-button"
            type="button"
            onClick={() => navigate("/grade-selection")}
          >
            Home
          </button>

          <button
            className="teacher-grade-nav-button active"
            type="button"
          >
            Teachers
          </button>

          <button
            className="teacher-grade-nav-button"
            type="button"
            onClick={() => navigate("/teacher-demo")}
          >
            Subjects
          </button>

          <button
            className="teacher-grade-nav-button"
            type="button"
            onClick={() => navigate("/profile")}
          >
            My Learning
          </button>
        </div>

        <div className="teacher-grade-navbar-right">
          {/* THEME */}
          <button
            className="teacher-grade-icon-button"
            type="button"
            title="Theme"
            onClick={() => setDarkMode((current) => !current)}
          >
            {darkMode ? (
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="4" />
                <path d="M12 2v2M12 20v2M4.93 4.93l1.42 1.42M17.65 17.65l1.42 1.42M2 12h2M20 12h2M4.93 19.07l1.42-1.42M17.65 6.35l1.42-1.42" />
              </svg>
            ) : (
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M21 12.8A8.5 8.5 0 1 1 11.2 3a6.7 6.7 0 0 0 9.8 9.8Z" />
              </svg>
            )}
          </button>

          {/* SEARCH */}
          <button
            className="teacher-grade-icon-button"
            type="button"
            title="Search"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="11" cy="11" r="7" />
              <line x1="16.5" y1="16.5" x2="21" y2="21" />
            </svg>
          </button>

          {/* NOTIFICATION */}
          <button
            className="teacher-grade-icon-button"
            type="button"
            title="Notifications"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9" />
              <path d="M10 21h4" />
            </svg>
          </button>

          {/* KIDS */}
          <button
            className="teacher-grade-kids-item"
            type="button"
            title="Switch account"
            onClick={() => setAccountName("Kids")}
          >
            <span className="teacher-grade-kids-logo">
              <span>kids</span>
            </span>

            <span className="teacher-grade-kids-text">
              {accountName}
            </span>

            <svg
              className="teacher-grade-kids-arrow"
              viewBox="0 0 10 10"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
            >
              <path d="M2 3.5 5 6.5 8 3.5" />
            </svg>
          </button>

          {/* PROFILE */}
          <div
            className="teacher-grade-profile"
            onClick={() => navigate("/profile")}
            title="Profile"
            role="button"
            tabIndex="0"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="8" cy="8" r="1.2" fill="currentColor" />
              <circle cx="16" cy="8" r="1.2" fill="currentColor" />
              <path d="M7 14 c2.2 2 7.8 2 10 0" />
            </svg>
          </div>
        </div>
      </nav>

      <main className="teacher-grade-content">
        <button
          type="button"
          className="teacher-grade-back"
          onClick={() => navigate("/grade-selection")}
        >
          ← Back to Teachers
        </button>

        <p className="teacher-grade-label">
          Teacher
        </p>

        <h1 className="teacher-grade-title">
          {teacher?.name || "Teacher"}
        </h1>

        <p className="teacher-grade-subtitle">
          Select a grade taught by this teacher
        </p>

        {loading ? (
          <div className="teacher-grade-loading">
            Loading grades...
          </div>
        ) : errorMessage ? (
          <div className="teacher-grade-empty">
            {errorMessage}
          </div>
        ) : displayGrades.length === 0 ? (
          <div className="teacher-grade-empty">
            No grades found for this teacher.
          </div>
        ) : (
          <div className="teacher-grade-row-wrapper">
            <div className="teacher-grade-row">
              {displayGrades.map((grade, index) => (
                <div
                  key={
                    grade.grade_id ||
                    `${grade.name}-${index}`
                  }
                  className="teacher-grade-card"
                  onClick={() => handleGradeClick(grade)}
                >
                  <div className="teacher-grade-number">
                    {String(index + 1).padStart(2, "0")}
                  </div>

                  <div className="teacher-grade-name">
                    {grade.name}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default TeacherGradeSelection;